import {alpha, createTheme, darken, lighten, type Theme} from '@mui/material/styles';
import type {ThemeConfig} from '../theme/themeConfig';

/**
 * Builds the MUI theme of a customer.
 *
 * The CI colors (primary, secondary, background, drawer) come from the customer's `theme.json`; everything
 * else - rounded corners, soft shadows, typography, the festive glow of the page background - is the same
 * for all customers and defined here.
 */
export function createAppTheme(config: ThemeConfig): Theme {
  const primary = config.primaryColor;
  const secondary = config.secondaryColor;
  const ink = '#1d2327';
  const inkSoft = '#5c666d';
  const line = alpha(ink, 0.08);
  const cardShadow = `0 1px 2px ${alpha(ink, 0.04)}, 0 12px 32px -16px ${alpha(ink, 0.22)}`;

  const base = createTheme({
    palette: {
      primary: {main: primary, contrastText: config.contrastText},
      secondary: {main: secondary, contrastText: config.contrastText},
      error: {main: '#c62828'},
      success: {main: '#2e7d32'},
      background: {default: config.backgroundColor, paper: '#ffffff'},
      text: {primary: ink, secondary: inkSoft},
      divider: line,
    },
    shape: {borderRadius: 14},
    typography: {
      fontFamily: '"Manrope Variable", "Segoe UI", Roboto, system-ui, -apple-system, sans-serif',
      h1: {fontWeight: 800, letterSpacing: '-0.02em'},
      h2: {fontWeight: 800, letterSpacing: '-0.02em'},
      h3: {fontWeight: 800, letterSpacing: '-0.02em'},
      h4: {fontWeight: 800, letterSpacing: '-0.02em', fontSize: '1.9rem'},
      h5: {fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.35rem'},
      h6: {fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.1rem', lineHeight: 1.3},
      subtitle1: {fontWeight: 600},
      subtitle2: {fontWeight: 600},
      body1: {lineHeight: 1.65},
      body2: {lineHeight: 1.6},
      button: {textTransform: 'none', fontWeight: 700, letterSpacing: 0},
      overline: {fontWeight: 700, letterSpacing: '0.12em'},
    },
  });

  return createTheme(base, {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: config.backgroundColor,
            backgroundImage: [
              `radial-gradient(900px 500px at -10% -10%, ${alpha(primary, 0.1)}, transparent 60%)`,
              `radial-gradient(700px 450px at 110% 0%, ${alpha(secondary, 0.12)}, transparent 60%)`,
            ].join(', '),
            backgroundAttachment: 'fixed',
            WebkitFontSmoothing: 'antialiased',
          },
          '::selection': {backgroundColor: alpha(secondary, 0.25)},
        },
      },
      MuiAppBar: {
        defaultProps: {elevation: 0},
        styleOverrides: {
          root: {
            backgroundImage: `linear-gradient(100deg, ${primary} 0%, ${darken(primary, 0.18)} 100%)`,
            boxShadow: `0 8px 24px -12px ${alpha(darken(primary, 0.4), 0.55)}`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {root: {minHeight: 64, '@media (min-width:600px)': {minHeight: 64}}},
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: config.drawerColor,
            borderRight: `1px solid ${line}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {rounded: {borderRadius: 20}},
      },
      MuiCard: {
        defaultProps: {elevation: 0},
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${line}`,
            boxShadow: cardShadow,
            overflow: 'hidden',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {root: {padding: 20, '&:last-child': {paddingBottom: 20}}},
      },
      MuiCardActions: {
        styleOverrides: {root: {padding: '0 20px 20px'}},
      },
      MuiButton: {
        defaultProps: {disableElevation: true},
        styleOverrides: {
          root: {borderRadius: 999, paddingLeft: 20, paddingRight: 20, minHeight: 40},
          contained: {
            '&:hover': {boxShadow: `0 10px 20px -10px ${alpha(ink, 0.45)}`},
          },
          outlined: {borderWidth: 1.5, '&:hover': {borderWidth: 1.5}},
        },
      },
      MuiIconButton: {
        styleOverrides: {root: {borderRadius: 12}},
      },
      MuiChip: {
        styleOverrides: {root: {fontWeight: 700, borderRadius: 999}},
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            minHeight: 44,
            marginLeft: 10,
            marginRight: 10,
            marginBottom: 2,
            paddingLeft: 12,
            paddingRight: 12,
            '&.Mui-selected': {
              backgroundColor: alpha(primary, 0.1),
              color: primary,
              '&:hover': {backgroundColor: alpha(primary, 0.14)},
              '& .MuiListItemIcon-root': {color: primary},
              '& .MuiListItemText-primary': {fontWeight: 700},
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {root: {minWidth: 40, color: inkSoft}},
      },
      MuiListItemText: {
        styleOverrides: {primary: {fontWeight: 600, fontSize: '0.95rem'}},
      },
      MuiDialog: {
        styleOverrides: {
          paper: {borderRadius: 24, boxShadow: `0 30px 60px -20px ${alpha(ink, 0.45)}`},
        },
      },
      MuiDialogTitle: {
        styleOverrides: {root: {fontWeight: 800, fontSize: '1.25rem'}},
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': {borderColor: alpha(ink, 0.18)},
          },
        },
      },
      MuiTextField: {
        defaultProps: {color: 'primary'},
      },
      MuiBadge: {
        styleOverrides: {badge: {fontWeight: 700}},
      },
      MuiDivider: {
        styleOverrides: {root: {borderColor: line}},
      },
      MuiMenu: {
        styleOverrides: {
          paper: {borderRadius: 16, boxShadow: `0 20px 40px -16px ${alpha(ink, 0.35)}`, border: `1px solid ${line}`},
        },
      },
      MuiMenuItem: {
        styleOverrides: {root: {borderRadius: 10, margin: '2px 6px', fontWeight: 600}},
      },
      MuiPopover: {
        styleOverrides: {paper: {borderRadius: 12}},
      },
      MuiCircularProgress: {
        defaultProps: {color: 'secondary'},
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {borderRadius: 999, height: 6, backgroundColor: alpha(secondary, 0.15)},
          bar: {borderRadius: 999, backgroundColor: lighten(secondary, 0.05)},
        },
      },
    },
  });
}
